import { MatchingViewModel } from "../ViewModels";
import { useMatchingEnricher } from "./useMatchingEnricher";
import {
    ActionStatusHelper,
    DomainDetailHook,
    useAppEnum,
    DOMAIN_CONFIGS,
    DomainHookProps,
    ActionResponse,
    useDetailLifeCycle,
} from "@/Shared";

import { useMatching } from "@/OneLiners";
import { MatchingModel } from "../MatchingModel";


/**
 * useMatchingDetail hook
 *
 * Domain-specific detail hook for Matching entities.
 * Acts as the orchestration layer between:
 * - domain data access and enrichment,
 * - permissions and access rights,
 * - UI feedback mechanisms (toasts, confirmations),
 * - navigation and redirection concerns.
 *
 * Responsibilities:
 * - Initialize the MatchingViewModel when data is available.
 * - Resolve computed fields and enrich the entity with cross-domain data.
 * - Provide a unified response and lifecycle for the UI layer.
 * - Expose entity VM, access rights, and public actions without exposing domain internals.
 *
 * @see DomainHookProps
 * @see DomainDetailHook<MatchingViewModel>
 *
 * @param props DomainHookProps
 * @returns DomainDetailHook<MatchingViewModel>
 *
 * Author: Samano CASTRE
 * Date: July 2025
 */
export function useMatchingDetail(props: DomainHookProps): DomainDetailHook<MatchingViewModel> {
    const { permissionHook, accessRight } = props;
    const { domainType } = DOMAIN_CONFIGS.Matching;

    /**
     * Matching-related enums exposed to the UI layer
     */
    const { MatchingEnums } = useAppEnum();

    /**
     * Matching domain data and lifecycle operations
     */
    const matchingHook = useMatching();

    /**
     * Domain enrichment layer
     *
     * Responsible for resolving computed fields
     * and cross-domain data for Matching entities
     */
    const {
        enricherResponse,
        getStrictEnrichedMatching,
    } = useMatchingEnricher();

    /**
     * Combined response acting as a single readiness
     * contract for initializing the detail view
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            matchingHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: matchingHook.stateResponse.error ??
            enricherResponse.error
    };

    /**
     * Detail life cycle hook
     *
     * Handles:
     * - Entity resolution (queryId / entityId)
     * - ViewModel construction
     * - Post-fetch or post-update handling
     * - Provides public API to the UI layer
     */
    const lifeCycleHook = useDetailLifeCycle({
        response,
        domainType,
        props,
        entityHook: matchingHook,
        buildVM: (currentEntity: MatchingModel, isDisplaying: boolean) => {
            return new MatchingViewModel({
                matching: getStrictEnrichedMatching(currentEntity),
                viewEnums: MatchingEnums,
                accessRight,
                isDisplaying
            })
        },
    });

    /**
     * Public API exposed to the UI layer
     */
    return {
        ...lifeCycleHook
    }
}